// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Forge Casino — Provably Fair Engine
// Cryptographic hash-based RNG that mathematically cannot be cheated
// ================================================================
// HOW IT WORKS:
// 1. Server generates serverSeed + nonce → SHA-256 hash (commitment)
// 2. Hash sent to player BEFORE spin
// 3. Player clicks SPIN → clientSeed added
// 4. Result = SHA-256(serverSeed + clientSeed + nonce) → mapped to reel positions
// 5. After spin: serverSeed revealed, player can verify hash matches
// 6. Every result logged for on-chain integrity proof
// ================================================================

// Use Web Crypto API (available in all modern browsers)
async function sha256(message) {
  const encoder = new TextEncoder();
  const data = encoder.encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

// Generate a cryptographically random seed
function generateSeed() {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array).map(b => b.toString(16).padStart(2, '0')).join('');
}

// Generate a random nonce
function generateNonce() {
  return Math.floor(Math.random() * 1000000000);
}

/**
 * ProvablyFairEngine — Core provably fair system
 *
 * Flow:
 *   const engine = new ProvablyFairEngine();
 *   const commitment = await engine.createCommitment();  // give hash to player
 *   const result = await engine.generateResult(clientSeed);  // after player spins
 *   const proof = engine.getProof();  // player can verify
 *   const isValid = await ProvablyFairEngine.verify(proof);  // anyone can verify
 */
class ProvablyFairEngine {
  constructor() {
    this.serverSeed = null;
    this.clientSeed = null;
    this.nonce = null;
    this.commitmentHash = null;
    this.resultHash = null;
    this.revealed = false;
  }

  /**
   * Step 1: Create commitment
   * Generates server seed + nonce, returns the commitment hash
   * This hash is shown to the player BEFORE they spin
   */
  async createCommitment() {
    this.serverSeed = generateSeed();
    this.nonce = generateNonce();
    this.revealed = false;
    this.clientSeed = null;
    this.resultHash = null;

    // Commitment = hash of serverSeed + nonce
    this.commitmentHash = await sha256(`${this.serverSeed}:${this.nonce}`);

    return {
      commitmentHash: this.commitmentHash,
      nonce: this.nonce,
    };
  }

  /**
   * Step 2: Generate result
   * Player provides their client seed, combined with server seed to produce result
   */
  async generateResult(clientSeed) {
    if (!this.serverSeed) throw new Error('Must create commitment first');
    if (this.revealed) throw new Error('Result already generated for this round');

    this.clientSeed = clientSeed || generateSeed();
    this.resultHash = await sha256(`${this.serverSeed}:${this.clientSeed}:${this.nonce}`);
    this.revealed = true;

    return {
      resultHash: this.resultHash,
      // Convert hash to a number between 0-1 for RNG
      resultValue: this.hashToFloat(this.resultHash),
      // Convert hash to multiple independent values (for multi-reel slots)
      resultValues: this.hashToMultipleFloats(this.resultHash, 5),
    };
  }

  /**
   * Convert a hex hash to a float between 0 and 1
   * Uses first 8 hex chars (32 bits) for precision
   */
  hashToFloat(hash) {
    const segment = hash.slice(0, 8);
    const value = parseInt(segment, 16);
    return value / 0xFFFFFFFF;
  }

  /**
   * Convert hash to multiple independent float values
   * Each value uses 8 hex chars from different positions
   */
  hashToMultipleFloats(hash, count) {
    const values = [];
    for (let i = 0; i < count; i++) {
      const start = (i * 8) % (hash.length - 8);
      const segment = hash.slice(start, start + 8);
      values.push(parseInt(segment, 16) / 0xFFFFFFFF);
    }
    return values;
  }

  /**
   * Step 3: Get proof for verification
   * Returns everything needed to verify this spin was fair
   */
  getProof() {
    if (!this.revealed) throw new Error('Must generate result first');

    return {
      serverSeed: this.serverSeed,
      clientSeed: this.clientSeed,
      nonce: this.nonce,
      commitmentHash: this.commitmentHash,
      resultHash: this.resultHash,
      timestamp: new Date().toISOString(),
    };
  }

  /**
   * Static: Verify a proof
   * Anyone can call this with a proof object to verify fairness
   */
  static async verify(proof) {
    const { serverSeed, clientSeed, nonce, commitmentHash, resultHash } = proof;

    // Verify commitment: hash(serverSeed:nonce) should equal commitmentHash
    const expectedCommitment = await sha256(`${serverSeed}:${nonce}`);
    if (expectedCommitment !== commitmentHash) {
      return { valid: false, reason: 'Commitment hash mismatch — server seed was changed after commitment' };
    }

    // Verify result: hash(serverSeed:clientSeed:nonce) should equal resultHash
    const expectedResult = await sha256(`${serverSeed}:${clientSeed}:${nonce}`);
    if (expectedResult !== resultHash) {
      return { valid: false, reason: 'Result hash mismatch — result was tampered with' };
    }

    return {
      valid: true,
      reason: 'Spin verified — mathematically provably fair',
      commitmentHash,
      resultHash,
    };
  }
}

export { ProvablyFairEngine, sha256, generateSeed };
export default ProvablyFairEngine;
